import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { 
  Search, 
  Filter, 
  ArrowUpRight, 
  Bookmark, 
  Briefcase,
  Clock,
  RotateCcw
} from 'lucide-react';

const UserPage = () => {
  // --- STATE DATA ---
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [savedIdeas, setSavedIdeas] = useState(JSON.parse(localStorage.getItem('savedIdeas')) || []);
  
  // --- STATE FILTER ---
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [maxBudget, setMaxBudget] = useState(10000000);
  const [sort, setSort] = useState('newest');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // 1. Fetch data saat mount
  useEffect(() => {
    fetchPublishedIdeas();
    
    // Load filter dari session storage HANYA sekali saat mount
    const sSearch = sessionStorage.getItem('f_search');
    const sCat = sessionStorage.getItem('f_cat');
    const sBudget = sessionStorage.getItem('f_budget');
    const sSort = sessionStorage.getItem('f_sort');

    if (sSearch) setSearch(sSearch);
    if (sCat) setCategory(sCat);
    if (sBudget) setMaxBudget(parseInt(sBudget));
    if (sSort) setSort(sSort);
  }, []);

  // 2. Jalankan filter & simpan history
  useEffect(() => {
    if (rawData.length > 0) {
      applyFilters();
      
      // Simpan untuk persistensi jika user kembali dari halaman detail
      sessionStorage.setItem('f_search', search);
      sessionStorage.setItem('f_cat', category);
      sessionStorage.setItem('f_budget', maxBudget.toString());
      sessionStorage.setItem('f_sort', sort);
    }
  }, [search, category, maxBudget, sort, rawData]);

  const fetchPublishedIdeas = async () => {
    const { data, error } = await supabase
      .from('ideas')
      .select('*')
      .eq('is_published', true)
      .order('created_at', { ascending: false });

    if (!error) {
      setRawData(data);
    }
  };

  const applyFilters = () => {
    let result = [...rawData];
    
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(item => 
        item.judul.toLowerCase().includes(term) || 
        item.tag.toLowerCase().includes(term)
      );
    }
    
    if (category !== 'All') {
      result = result.filter(item => item.tag === category);
    }
    
    if (maxBudget < 10000000) {
      result = result.filter(item => item.modal_min <= maxBudget);
    }
    
    const sorted = [...result];
    if (sort === 'low-price') sorted.sort((a, b) => a.modal_min - b.modal_min);
    else if (sort === 'high-price') sorted.sort((a, b) => b.modal_min - a.modal_min);
    else if (sort === 'newest') sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredData(sorted);
  };

  const resetFilters = (e) => {
    if(e) e.preventDefault();
    setSearch('');
    setCategory('All');
    setMaxBudget(10000000);
    setSort('newest');
    sessionStorage.clear();
  };

  const toggleSave = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    let updated = [...savedIdeas];
    if (updated.includes(id)) updated = updated.filter(i => i !== id);
    else updated.push(id);
    setSavedIdeas(updated);
    localStorage.setItem('savedIdeas', JSON.stringify(updated));
  };

  const formatRp = (v) => new Intl.NumberFormat('id-ID', { 
    style: 'currency', 
    currency: 'IDR', 
    maximumFractionDigits: 0 
  }).format(v);

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-slate-900 font-['Onest']">
      
      {/* --- NAVBAR --- */}
      <nav className="bg-white/80 backdrop-blur-xl sticky top-0 z-[50] border-b border-slate-100 h-16 md:h-20 flex items-center">
        <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2.5 text-2xl md:text-4xl font-semibold text-emerald-800">
            mikropedia
          </Link>

          <div className="flex items-center gap-2 md:gap-4">
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setIsFilterOpen(!isFilterOpen);
              }}
              className={`md:hidden p-2.5 rounded-xl transition-all border ${isFilterOpen ? 'bg-emerald-800 text-white border-emerald-800' : 'bg-white text-slate-900 border-slate-100 shadow-sm'}`}
            >
              <Filter size={18} />
            </button>

            <Link to="/saved" className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-[10px] md:text-xs transition-all border ${savedIdeas.length > 0 ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-400 border-slate-100'}`}>
              <Bookmark size={14} fill={savedIdeas.length > 0 ? "white" : "none"} />
              <span>{savedIdeas.length} <span className="hidden sm:inline">Tersimpan</span></span>
            </Link>
          </div>
        </div>
      </nav>

      {/* --- FILTER SECTION --- */}
      <div className={`
        sticky top-16 md:top-20 z-[40] 
        bg-white/95 backdrop-blur-sm border-b border-slate-100 
        transition-all duration-300 ease-in-out overflow-hidden
        ${isFilterOpen ? 'max-h-[600px] opacity-100 py-4 shadow-lg' : 'max-h-0 opacity-0 md:max-h-none md:opacity-100 md:py-4'}
        md:max-h-none md:opacity-100 md:py-4 md:shadow-none
      `}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 p-2 md:p-3 bg-slate-50 rounded-[1.5rem] md:rounded-[2rem] border border-slate-200/50">
            
            {/* Search */}
            <div className="relative col-span-2 md:col-span-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input 
                type="text" placeholder="Cari ide..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white rounded-xl text-[12px] md:text-[14px] font-bold outline-none border border-slate-200/50" 
              />
            </div>

            {/* Category */}
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-3 bg-white rounded-xl text-[12px] font-extrabold text-slate-700 outline-none border border-slate-200/50 appearance-none shadow-sm cursor-pointer">
              <option value="All">Semua Kategori</option>
              <option value="Digital">Digital</option>
              <option value="Kuliner">Kuliner</option>
              <option value="Jasa">Jasa</option>
              <option value="Agri">Agri</option>
            </select>

            {/* Sort */}
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="w-full px-3 py-3 bg-white rounded-xl text-[12px] font-extrabold text-slate-700 outline-none border border-slate-200/50 appearance-none shadow-sm cursor-pointer">
              <option value="newest">Terbaru</option>
              <option value="low-price">Modal Terendah</option>
              <option value="high-price">Modal Tertinggi</option>
            </select>

            {/* Budget Range */}
            <div className="col-span-2 md:col-span-1 bg-white rounded-xl px-4 py-2 border border-slate-200/50 flex flex-col justify-center shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <span className="text-[9px] font-black text-slate-400 uppercase">Budget Max</span>
                <span className="text-emerald-700 font-black text-[10px]">{formatRp(maxBudget)}</span>
              </div>
              <input type="range" min="500000" max="10000000" step="500000" value={maxBudget} onChange={(e) => setMaxBudget(parseInt(e.target.value))} className="w-full h-1.5 accent-emerald-800 cursor-pointer" />
            </div>

            {/* Reset Button */}
            <button 
              type="button"
              onClick={resetFilters}
              className="col-span-2 md:col-span-1 flex items-center justify-center gap-2 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-xl py-3 transition-all text-[12px] font-bold"
            >
              <RotateCcw size={14} />
              Reset Filter
            </button>
          </div>
        </div>
      </div>

      {/* --- GRID KONTEN --- */}
      <main className="container mx-auto py-6 md:py-10 px-4 md:px-6">
        <div className="flex items-center gap-3 mb-6 md:mb-10">
          <div className="w-8 md:w-12 h-[1px] bg-slate-200"></div>
          <h3 className="text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-400">
            {filteredData.length} Ide Tersedia
          </h3>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-10">
          {filteredData.map((item) => (
            <Link 
              key={item.id} 
              to={`/ide/${item.slug}`} 
              className="group bg-white rounded-[1.2rem] md:rounded-[2.5rem] border border-slate-100 overflow-hidden flex flex-col transition-all duration-500 hover:shadow-xl hover:-translate-y-1 md:hover:-translate-y-2"
            >
              <div className="relative aspect-[4/3] md:h-64 overflow-hidden">
                <img 
                  src={item.img_url || 'https://images.unsplash.com/photo-1556740734-7f382e7e0e4e?w=800'} 
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" 
                  alt={item.judul} 
                />
                <button 
                  type="button"
                  onClick={(e) => toggleSave(e, item.id)}
                  className={`absolute top-2 right-2 md:top-6 md:right-6 p-2 md:p-3 rounded-lg md:rounded-2xl transition-all shadow-lg ${savedIdeas.includes(item.id) ? 'bg-emerald-800 text-white' : 'bg-white/90 text-slate-400'}`}
                >
                  <Bookmark size={14} className="md:w-[18px] md:h-[18px]" fill={savedIdeas.includes(item.id) ? "currentColor" : "none"} />
                </button>
              </div>

              <div className="p-3 md:p-6 flex-grow flex flex-col">
                <h4 className="text-[15px] md:text-lg font-bold text-slate-900 mb-3 md:mb-4 leading-tight line-clamp-2 group-hover:text-emerald-800 transition-colors">
                  {item.judul}
                </h4>

                <div className="mt-auto pt-2 md:pt-2 border-t border-slate-50 flex justify-between items-center">
                  <div>
                    <p className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5 md:mb-1">Modal</p>
                    <p className="text-[16px] md:text-xl font-black text-slate-900">{formatRp(item.modal_min)}</p>
                  </div>
                  <div className="w-7 h-7 md:w-12 md:h-12 rounded-lg md:rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-emerald-800 group-hover:text-white transition-all duration-300">
                    <ArrowUpRight size={14} className="md:w-5 md:h-5" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {filteredData.length === 0 && (
          <div className="text-center py-16 bg-white rounded-[2rem] border-2 border-dashed border-slate-200">
             <Search className="mx-auto text-slate-300 mb-4" size={32} />
             <h3 className="text-lg font-bold text-slate-900">pencarian...</h3>
             <button onClick={resetFilters} className="mt-4 text-emerald-800 font-bold underline">Lihat Semua Ide</button>
          </div>
        )}
      </main>

      {/* --- FOOTER --- */}
      <footer className="bg-emerald-900 py-16 text-white mt-20 font-['Onest']">
      <div className="container mx-auto px-6">
        {/* Grid Utama: Menggunakan 3 kolom di Desktop untuk keseimbangan */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12 items-start">
          
          {/* Kolom 1: Brand & Slogan */}
          <div className="md:col-span-1">
            <Link to="/" className="text-3xl font-semibold tracking-tight mb-4 block">
              mikropedia
            </Link>
            <p className="text-emerald-100/60 text-sm leading-relaxed max-w-xs">
              Platform untuk membantu UMKM Indonesia menemukan peluang usaha dengan modal terjangkau.
            </p>
          </div>

          {/* Kolom 2: Navigasi (Tengah) */}
          <div className="flex flex-col gap-4">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Navigasi</h4>
            <nav className="flex flex-col gap-3 font-bold text-sm">
              <Link to="/about" className="hover:text-emerald-300 transition-colors">Tentang Kami</Link>
              <Link to="/contact" className="hover:text-emerald-300 transition-colors">Hubungi Kami</Link>
              <Link to="/privacy" className="hover:text-emerald-300 transition-colors">Kebijakan Privasi</Link>
            </nav>
          </div>

          {/* Kolom 3: Info Tambahan / Kontak Singkat */}
          <div className="flex flex-col gap-4">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Kontak</h4>
            <p className="text-sm font-bold opacity-80">auxindo.app@gmail.com</p>
            <div className="flex gap-4 mt-2">
              {/* Placeholder Sosmed Icon jika perlu */}
              <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 cursor-pointer transition-all">
                <span className="text-[10px]">IG</span>
              </div>
              <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 cursor-pointer transition-all">
                <span className="text-[10px]">FB</span>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Footer */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[11px] font-medium text-emerald-100/30 uppercase tracking-widest">
            &copy; 2026 mikropedia &bull; all rights reserved
          </p>
          <div className="flex gap-6 text-[11px] font-bold text-emerald-100/30 uppercase tracking-widest">
            <span>Untuk Indonesia</span>
          </div>
        </div>
      </div>
    </footer>

    </div>
  );
};

export default UserPage;